// Step timing for the Save & Submit critical path.
//
// WHY: the wait is a chain of sequential network calls, and until this existed the only
// way to reason about it was to COUNT round-trips and multiply by an assumed latency.
// Measured NetSuite latency is ~0.4-0.6s for a SuiteQL query and ~1.3s for a REST record
// load, so a count is a decent estimate — but an estimate is not a measurement, and the
// steps that dominate are not always the ones with the most calls.
//
// Diagnostic ONLY. Every path here is non-throwing: a timer must never be the reason a
// save fails, and `step` re-throws the original error untouched so control flow is
// identical whether timing is present or not.
//
// Output goes to the browser console under [cpq2 timing]. It is not shown in the UI —
// the operator does not need it and the modal is already busy.
//
// It is ALSO persisted, via report() below (Kevin, 2026-08-24). A console line only exists
// for whoever had devtools open, which in practice is nobody at the moment it matters: a
// save that took 40 seconds and worked leaves no trace, so it never gets fixed. report()
// sends the same breakdown to cpq2_timing.

import { recordLifecycle, type LifecycleOutcome } from "./lifecycle";

export type StepTiming = {
  name: string;
  ms: number;
  /** Server-reported detail, when the endpoint returns its own breakdown. */
  server?: ServerTiming | null;
  failed?: boolean;
};

/** The shape api/shared/netsuite.js withTiming() puts on a response. */
export type ServerTiming = {
  wallMs: number;
  netsuiteMs: number;
  otherMs: number;
  calls: number;
  slowest?: Array<{ label: string; ms: number; attempts?: number; status?: number }>;
  repeated?: Array<{ label: string; n: number; ms: number }>;
};

const now = (): number =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

export type Timer = ReturnType<typeof createTimer>;

export function createTimer(label: string) {
  const t0 = now();
  const steps: StepTiming[] = [];

  /** Time one awaited step. Records on failure too — a slow step that then throws is
   *  exactly the one worth seeing, and dropping it would bias the picture toward
   *  successes. */
  const step = async <T>(name: string, fn: () => Promise<T>): Promise<T> => {
    const s = now();
    try {
      const out = await fn();
      const ms = Math.round(now() - s);
      // Endpoints wrapped in withTiming() return their own breakdown; fold it in so the
      // client step and the server's view of the same call sit side by side.
      const server =
        out && typeof out === "object" && "timing" in (out as Record<string, unknown>)
          ? ((out as Record<string, unknown>).timing as ServerTiming)
          : null;
      steps.push({ name, ms, server });
      return out;
    } catch (e) {
      steps.push({ name, ms: Math.round(now() - s), failed: true });
      throw e; // untouched: timing must not alter control flow
    }
  };

  /** Record a duration measured elsewhere (e.g. a step that is not a single await). */
  const add = (name: string, ms: number) => {
    steps.push({ name, ms: Math.round(ms) });
  };

  const totalMs = () => Math.round(now() - t0);

  /** Print the breakdown. Slowest-first is deliberate: the ordered list reads like the
   *  flow, but the question being asked is always "what do I fix first". */
  const log = () => {
    try {
      const total = totalMs();
      const measured = steps.reduce((a, s) => a + s.ms, 0);
      const lines = steps.map(
        (s) =>
          `  ${String(s.ms).padStart(6)} ms  ${((s.ms / Math.max(total, 1)) * 100)
            .toFixed(0)
            .padStart(3)}%  ${s.name}${s.failed ? "  (FAILED)" : ""}` +
          (s.server
            ? `\n           server: ${s.server.calls} NetSuite call(s), ` +
              `${s.server.netsuiteMs} ms waiting on NetSuite, ${s.server.otherMs} ms other`
            : "")
      );
      console.log(
        `[cpq2 timing] ${label}: ${total} ms total\n` +
          lines.join("\n") +
          `\n  ${String(Math.max(0, total - measured)).padStart(6)} ms       unmeasured (local work between steps)`
      );
      // The slowest server calls across the whole submit, which is what a round-trip
      // reduction has to target.
      const calls = steps
        .flatMap((s) => (s.server?.slowest ?? []).map((c) => ({ ...c, step: s.name })))
        .sort((a, b) => b.ms - a.ms)
        .slice(0, 12);
      if (calls.length) {
        console.log(
          `[cpq2 timing] slowest NetSuite calls:\n` +
            calls.map((c) => `  ${String(c.ms).padStart(6)} ms  ${c.step} :: ${c.label}`).join("\n")
        );
      }
      const repeated = steps.flatMap((s) => s.server?.repeated ?? []);
      if (repeated.length) {
        console.log(
          `[cpq2 timing] repeated NetSuite calls (candidates to cache or batch):\n` +
            repeated
              .sort((a, b) => b.ms - a.ms)
              .map((r) => `  x${r.n}  ${String(r.ms).padStart(6)} ms total  ${r.label}`)
              .join("\n")
        );
      }
    } catch (e) {
      /* never let logging break a save */
    }
  };

  /**
   * Persist this run to cpq2_timing. Fire-and-forget; never throws.
   *
   * `reason` defaults to naming the step that dominated, because that is the answer to the
   * only question anyone asks of a slow save. A failed step sets the outcome to error even
   * when the caller does not say so — a run containing a failure is not an "ok" run, and
   * letting it be recorded as one would quietly inflate the success rate.
   */
  const report = (opts: {
    lifecycle?: string;
    outcome?: LifecycleOutcome;
    reason?: string | null;
    product?: string | null;
    configMode?: string | null;
    itemId?: string | number | null;
    itemName?: string | null;
    fingerprint?: string | null;
    context?: Record<string, unknown>;
  } = {}) => {
    try {
      const total = totalMs();
      const failed = steps.find((s) => s.failed);
      const slowest = steps.reduce<StepTiming | null>((a, s) => (!a || s.ms > a.ms ? s : a), null);
      recordLifecycle({
        lifecycle: opts.lifecycle || label,
        outcome: opts.outcome ?? (failed ? "error" : "ok"),
        totalMs: total,
        reason:
          opts.reason ??
          (failed
            ? `failed at ${failed.name}`
            : slowest
              ? `${slowest.name} took ${slowest.ms} ms of ${total} ms`
              : null),
        steps: steps.map((s) => ({ name: s.name, ms: s.ms, failed: s.failed, server: s.server ?? undefined })),
        product: opts.product ?? null,
        configMode: opts.configMode ?? null,
        itemId: opts.itemId ?? null,
        itemName: opts.itemName ?? null,
        fingerprint: opts.fingerprint ?? null,
        context: {
          ...(opts.context || {}),
          // Server-side NetSuite time across the whole run, so "we were waiting on NetSuite"
          // can be separated from "we were slow ourselves" without unpacking every step.
          netsuiteMs: steps.reduce((a, s) => a + (s.server?.netsuiteMs ?? 0), 0),
          netsuiteCalls: steps.reduce((a, s) => a + (s.server?.calls ?? 0), 0),
        },
      });
    } catch {
      /* measurement must never break a save */
    }
  };

  return { step, add, totalMs, log, report, steps };
}
